import { Injectable, inject } from '@angular/core';
import { EpikPaymentMethod, FundraiserMinimal } from '@fmnts/api';
import {
  Campaign,
  CampaignField,
  CampaignFieldset,
  isCampaignSelectField,
} from '@fmnts/api/next';
import { isNonEmptyString, isRecord, isString } from '@fmnts/core';
import { Decimal, Numeric } from '@fmnts/core/decimal';
import { TranslationService } from '@fmnts/i18n';
import { RaiseNowSettings } from '@formunauts/shared/vendors/raisenow/data-access';
import { PaymentConfig } from '@raisenow/epik';
import {
  RaiseNowCustomParametersSalesforce,
  RaiseNowCustomParametersSextant,
  RaiseNowEppCrmFields,
  RaiseNowPaymentData,
  RaiseNowPaymentSubscription,
  RaiseNowRecurringInterval,
  RaiseNowSupporterInformation,
  RaiseNowSupporterMetadataSalesForce,
} from './raisenow.types';

export interface RaiseNowExtraFields {
  /** API key for accessing raisenow API */
  raisenowApiKey?: string;
  /** `true`, if the raisenow test mode should be used */
  raisenowTestMode?: boolean;
  /**  */
  raisenowUseStripe?: boolean;
  raisenowUseSalesforce?: boolean;

  email_fallback_address?: string;
  stored_campaign_id?: string;

  skipCvv?: boolean;

  title?: any;
  dni?: string;
  additionalFieldsMapping?: any;
  occupationLabel?: string | null;
  academicTitleLabel?: string | null;
  raisenowCurrencyCode?: string;

  stored_fundraising_team_type?: unknown;
  stored_crm_fundraising_team?: unknown;
  stored_customer_email_permission?: PaymentConfig['stored_customer_email_permission'];

  // Fundraiser information
  fundraiserCode?: any;
  fundraiserExternalId?: any;
  fundraiser_firstname?: string;
  fundraiser_lastname?: string;
  fundraiser_email?: string;

  // Fields from general payment data object
  accountUuid?: RaiseNowPaymentData['account_uuid'];
  profile?: RaiseNowPaymentData['profile'];
  statementDescriptor?: RaiseNowPaymentData['statement_descriptor'];

  // Fields from general payment subscription object
  timezone?: RaiseNowPaymentSubscription['timezone'];
}

interface RaisenowBasePaymentData {
  test_mode?: boolean;
  subscription?: Partial<RaiseNowPaymentData['subscription']>;
  supporter_metadata?: Partial<RaiseNowSupporterInformation['metadata']>;
  custom_parameters?:
    | RaiseNowCustomParametersSalesforce
    | RaiseNowCustomParametersSextant;
}

/**
 * Base for a payment object that uses `EPP`.
 */
interface RaiseNowEppPaymentDataBase {
  test_mode?: PaymentConfig['test_mode'];
  amount?: PaymentConfig['amount'];
  currency?: string;
  immediate_execution: PaymentConfig['immediate_execution'];
  recurring: PaymentConfig['recurring'];
  recurring_interval?: PaymentConfig['recurring_interval'];

  language: PaymentConfig['language'];
}

/**
 * Interface for the payment configuration that is passed
 * to creating an EPP payment.
 */
type RaisenowEppPaymentData = RaiseNowEppPaymentDataBase &
  RaiseNowEppCrmFields & {
    // Not sure if these are correct or not
    email?: string;
    stored_first_name?: string;
    stored_last_name?: string;
    stored_street?: string;
    stored_house_number?: string;
    stored_city?: string;
    stored_zip_code?: string;
  };

@Injectable({
  providedIn: 'root',
})
export class RaiseNowUtilService {
  private readonly translationService = inject(TranslationService);
  private readonly rnwSettings = inject(RaiseNowSettings);

  /**
   * once the RaiseNow process is complete, copy the result fields to the object we upload
   * (note: called from different places)
   * @param  fromObject an object containing the result RaiseNow fields
   * @param  toObject   an object to which the result RaiseNow fields are copied
   */
  addRaiseNowProperties(fromObject, toObject) {
    // EPP
    if ('epp_transaction_id' in fromObject) {
      toObject.raisenow_epp_transaction_id = fromObject.epp_transaction_id;
    }
    // - we need to check for values with the raisenow_ prefix as well - see donationSubmissionService.prepareFormData
    if ('raisenow_epp_transaction_id' in fromObject) {
      toObject.raisenow_epp_transaction_id =
        fromObject.raisenow_epp_transaction_id;
    }
    if ('subscription_token' in fromObject) {
      toObject.raisenow_subscription_token = fromObject.subscription_token;
    }
    if ('raisenow_subscription_token' in fromObject) {
      toObject.raisenow_subscription_token =
        fromObject.raisenow_subscription_token;
    }
  }

  /**
   * Formats the given `value` to a `number` as it is expected to be
   * passed to the raisenow API.
   *
   * @param value
   * The value to format.
   * Might be anything that is understood by the `Decimal` constructor.
   *
   * @returns
   * Formatted value
   */
  formatAmount(value: Numeric): number {
    return new Decimal(value).times(100).toDecimalPlaces(0).toNumber();
  }

  /**
   * get the appropriate donation amount or donation amount per interval,
   * depending on the type of payment selected (one-off or recurring)
   * @param  formData the form
   * @return          a correctly formatted amount
   */
  getDonationAmount(formData: Record<string, any>): number {
    let amount: string;
    if (
      'contract_start_date' in formData &&
      'direct_debit_interval' in formData &&
      this.getRecurringIntervalCode(
        formData.contract_start_date,
        formData.direct_debit_interval,
      ) &&
      'donation_amount_per_interval' in formData
    ) {
      // it's a recurring payment
      amount = formData.donation_amount_per_interval;
    } else if ('donation_amount' in formData) {
      // it's a one-off payment
      amount = formData.donation_amount;
    }
    return this.formatAmount(amount);
  }

  /**
   * @returns
   * Formatted currency code for raisw now
   */
  formatCurrencyCode(currencyCode: string) {
    return isString(currencyCode) ? currencyCode.toLowerCase() : '';
  }

  /**
   * if an object has another object as its property, move all its fields to the root object
   * @param  object             [description]
   * @param  propertyObjectName [description]
   * @return                    [description]
   */
  _moveFieldsFromObjectToRoot(object, propertyObjectName) {
    if (propertyObjectName in object) {
      for (const [key, value] of Object.entries(object[propertyObjectName])) {
        object[key] = value;
      }
    }
    delete object[propertyObjectName];
  }

  /**
   * build the legacy RaiseNow EPP object
   * @param  formData            the form object
   * @param  raiseNowExtraFields field with extra fields RaiseNow needs created in getRaiseNowExtraFields
   * @param  epikConfig          the configuration obtained from RaiseNow's EPIK API
   * @return                     a payment object we can pass to RaiseNow
   */
  createEpikPaymentObjectEPP(
    formData: Record<string, any>,
    raiseNowExtraFields: Readonly<RaiseNowExtraFields>,
    epikConfig: any,
  ): RaisenowEppPaymentData {
    const epikPaymentData: RaisenowEppPaymentData =
      this._createEpikPaymentObjectBase(formData, raiseNowExtraFields) as any;

    // Donation data
    if ('donation_amount' in formData) {
      epikPaymentData.amount = this.getDonationAmount(formData);
    }
    if ('raisenowCurrencyCode' in raiseNowExtraFields) {
      epikPaymentData.currency =
        raiseNowExtraFields.raisenowCurrencyCode.toUpperCase();
    }
    // Meta data for CRMs
    this._appendCrmDataForEpp(epikPaymentData, raiseNowExtraFields);

    // Supporter information
    if ('email' in formData) {
      epikPaymentData.stored_customer_email = epikPaymentData.email =
        formData.email;
    }
    if ('title' in raiseNowExtraFields) {
      epikPaymentData.stored_customer_salutation = raiseNowExtraFields.title;
    }
    if ('first_name' in formData) {
      epikPaymentData.stored_customer_firstname =
        epikPaymentData.stored_first_name = formData.first_name;
    }
    if ('last_name' in formData) {
      epikPaymentData.stored_customer_lastname =
        epikPaymentData.stored_last_name = formData.last_name;
    }
    if ('street' in formData) {
      epikPaymentData.stored_customer_street = epikPaymentData.stored_street =
        formData.street + ' ' + formData.house_number;
    }
    if ('house_number' in formData) {
      epikPaymentData.stored_house_number = formData.house_number;
      if ('address_addition' in formData) {
        epikPaymentData.stored_house_number += `/${formData.address_addition}`;
      }
      if ('address_addition_2' in formData) {
        epikPaymentData.stored_house_number += `/${formData.address_addition_2}`;
      }
    }
    if ('zip_code' in formData) {
      epikPaymentData.stored_customer_zip_code =
        epikPaymentData.stored_zip_code = formData.zip_code;
    }
    if ('city' in formData) {
      epikPaymentData.stored_customer_city = epikPaymentData.stored_city =
        formData.city;
    }
    if ('country' in formData) {
      epikPaymentData.stored_customer_country = formData.country;
    }
    if ('phone_number' in formData || 'mobile_number' in formData) {
      epikPaymentData.stored_customer_phone =
        formData.phone_number || formData.mobile_number;
    }
    this._moveFieldsFromObjectToRoot(epikPaymentData, 'subscription');
    if ('recurring_interval' in epikPaymentData) {
      epikPaymentData.immediate_execution = false;
      epikPaymentData.recurring = true;
    } else {
      epikPaymentData.immediate_execution = true;
      epikPaymentData.recurring = false;
    }
    this._moveFieldsFromObjectToRoot(epikPaymentData, 'custom_parameters');
    this._moveFieldsFromObjectToRoot(epikPaymentData, 'supporter_metadata');
    if ('stored_customer_language' in epikPaymentData) {
      epikPaymentData.language = epikPaymentData.stored_customer_language;
    }
    return epikPaymentData;
  }

  /**
   * create the base RaiseNow payment object common to both their legacy EPP and their newer EPMS system
   * @param  formData            the form object
   * @param  raiseNowExtraFields field with extra fields RaiseNow needs created in getRaiseNowExtraFields
   * @return                     a payment object we can pass to RaiseNow
   */
  private _createEpikPaymentObjectBase(
    formData: Record<string, any>,
    raiseNowExtraFields: Readonly<RaiseNowExtraFields>,
  ): RaisenowBasePaymentData {
    const epikPaymentData: RaisenowBasePaymentData = {};

    // test mode (may be overwritten by campaign config)
    epikPaymentData.test_mode = raiseNowExtraFields.raisenowTestMode;

    // subscription -------------
    const subscription: Partial<RaiseNowPaymentSubscription> = {};
    epikPaymentData.subscription = subscription;
    // set interval for payment methods that support recurring
    if (
      [
        EpikPaymentMethod.CreditCardViaEpp,
        EpikPaymentMethod.MposViaWebTerminal,
      ].includes(formData.payment_method) &&
      'contract_start_date' in formData &&
      'direct_debit_interval' in formData
    ) {
      const recurringIntervalCode = this.getRecurringIntervalCode(
        formData.contract_start_date,
        formData.direct_debit_interval,
      );
      if (recurringIntervalCode) {
        subscription.recurring_interval = recurringIntervalCode;
      }
    }
    subscription.timezone = raiseNowExtraFields.timezone || 'America/Santiago';
    if (
      'raisenowUseSalesforce' in raiseNowExtraFields &&
      raiseNowExtraFields.raisenowUseSalesforce
    ) {
      // the Salesforce fields transferred to RaiseNow
      // ---------------------------------------------
      const custom_parameters: RaiseNowCustomParametersSalesforce = {};
      const supporter_metadata: RaiseNowSupporterMetadataSalesForce = {};
      if (raiseNowExtraFields.stored_campaign_id) {
        custom_parameters.campaign_id = raiseNowExtraFields.stored_campaign_id;
      }
      if ('location' in formData) {
        custom_parameters.marketing_location_identifier = `${formData.location}`;
      }
      if (raiseNowExtraFields.stored_fundraising_team_type) {
        custom_parameters.fundraising_team_type =
          raiseNowExtraFields.stored_fundraising_team_type;
      }
      if (raiseNowExtraFields.stored_crm_fundraising_team) {
        custom_parameters.crm_fundraiser_team_id =
          raiseNowExtraFields.stored_crm_fundraising_team;
      }
      if (raiseNowExtraFields.fundraiserExternalId) {
        custom_parameters.crm_fundraiser_id =
          raiseNowExtraFields.fundraiserExternalId;
      }
      // if (raiseNowExtraFields.fundraiser_firstname) {
      //   custom_parameters.fundraiser_firstname =
      //     raiseNowExtraFields.fundraiser_firstname;
      // }
      // if (raiseNowExtraFields.fundraiser_lastname) {
      //   custom_parameters.fundraiser_lastname =
      //     raiseNowExtraFields.fundraiser_lastname;
      // }
      // Supporter metadata
      supporter_metadata.email_contact_allowed = 'false';
      if ('mobile_number' in formData) {
        supporter_metadata.mobile_phone = formData.mobile_number;
      }
      if ('province' in formData) {
        supporter_metadata.region_level_1 = formData.province;
      }
      if (raiseNowExtraFields.occupationLabel) {
        supporter_metadata.sf_Contact_Occupation__c =
          raiseNowExtraFields.occupationLabel;
      }
      // if ('address_addition' in formData) {
      //   supporter_metadata.address_addendum = formData.address_addition;
      //   if ('address_addition_2' in formData && formData.address_addition_2) {
      //     supporter_metadata.address_addendum +=
      //       '/' + formData.address_addition_2;
      //   }
      // }
      if (raiseNowExtraFields.additionalFieldsMapping) {
        Object.entries(raiseNowExtraFields.additionalFieldsMapping).forEach(
          ([key, value]) => {
            supporter_metadata['sf_Contact_' + key + '__c'] = value;
          },
        );
      }
      epikPaymentData.custom_parameters = custom_parameters;
      epikPaymentData.supporter_metadata = supporter_metadata; // to move to supporter later
    } else {
      // the Sextant fields transferred to RaiseNow
      // ---------------------------------------------
      const custom_parameters: RaiseNowCustomParametersSextant = {};
      if ('contract_start_date' in formData) {
        custom_parameters.stored_sxt_first_charge_date =
          formData.contract_start_date;
      }
      if (raiseNowExtraFields.fundraiserCode) {
        custom_parameters.stored_sxt_vendor_id =
          raiseNowExtraFields.fundraiserCode;
      }
      if (custom_parameters.stored_sxt_vendor_id) {
        custom_parameters.stored_sxt_base_record_number =
          custom_parameters.stored_sxt_vendor_id + '_donation__' + Date.now();
      }
      // TODO: see filterAllSextantKeywords in the old frontend
      // if ('stored_sxt_partner_keywords' in formData) {
      //   custom_parameters.stored_sxt_partner_keywords = JSON.stringify(
      //     formData.stored_sxt_partner_keywords
      //   );
      // }
      if ('location' in formData) {
        custom_parameters.stored_sxt_marketing_location = `Fields(Werbeort=${formData.location})`;
      }
      if (raiseNowExtraFields.email_fallback_address) {
        custom_parameters.fallbackEmail =
          raiseNowExtraFields.email_fallback_address;
      }
      if (raiseNowExtraFields.email_fallback_address) {
        custom_parameters.fallbackEmail =
          raiseNowExtraFields.email_fallback_address;
      }
      if (raiseNowExtraFields.stored_campaign_id) {
        custom_parameters.stored_campaign_id =
          raiseNowExtraFields.stored_campaign_id;
      }

      epikPaymentData.custom_parameters = custom_parameters;
    }

    return epikPaymentData;
  }

  /**
   * Appends meta data that is used to connect a CRM dataset to the EPP payment.
   * For EPP the naming of the properties for the CRMs shouldn't depend on the
   * CRM that is used.
   *
   * @param payment Original payment object
   * @param raiseNowExtraFields
   *
   * @returns
   * The modified payment object with the appended data
   */
  private _appendCrmDataForEpp(
    payment: RaisenowEppPaymentData,
    raiseNowExtraFields: Readonly<RaiseNowExtraFields>,
  ): RaisenowEppPaymentData {
    if ('stored_campaign_id' in raiseNowExtraFields) {
      payment.stored_campaign_id = raiseNowExtraFields.stored_campaign_id;
    }
    if ('stored_fundraising_team_type' in raiseNowExtraFields) {
      payment.stored_fundraising_team_type =
        raiseNowExtraFields.stored_fundraising_team_type;
    }
    if ('stored_crm_fundraising_team' in raiseNowExtraFields) {
      payment.stored_crm_fundraising_team =
        raiseNowExtraFields.stored_crm_fundraising_team;
    }
    if ('stored_customer_email_permission' in raiseNowExtraFields) {
      payment.stored_customer_email_permission =
        raiseNowExtraFields.stored_customer_email_permission;
    }

    return payment;
  }

  /**
   * @param campaign The campaign
   * @param key The key of the field in the campaign
   * @param value The value if an item in that field
   *
   * @returns
   * The translated label for the field item.
   * It will return `''` in case
   *  - there is no field with the given `key`
   *  - this field is not a CampaignSelectField
   *  - if it does not have an item with the given `value`
   */
  getFieldItemLabel(campaign: Campaign, key: string, value: any): string {
    const targetField = campaign.pages
      .flatMap((page) =>
        page.fieldsets.map((fieldset) =>
          fieldset.fields.find((field) => field.key === key),
        ),
      )
      .reduce((acc, curr) => acc || curr);
    if (targetField) {
      if (isCampaignSelectField(targetField)) {
        const fieldItem = targetField.widgetOptions.items.find(
          (item) => item.value === value,
        );
        if (fieldItem) {
          return this.translationService.getInlineTranslation(fieldItem.label);
        }
      }
    }

    return '';
  }

  getRecurringIntervalCode(
    referenceDate,
    intervalKey,
  ): RaiseNowRecurringInterval {
    const day = new Date(referenceDate).getDate();
    const month = new Date(referenceDate).getMonth() + 1;
    let recurringInterval = null;
    switch (intervalKey) {
      case '0':
        recurringInterval = 'monthly';
        break;
      case '1':
        recurringInterval = 'quarterly';
        break;
      case '2':
        recurringInterval = 'semestral';
        break;
      case '3':
        recurringInterval = 'yearly';
        break;
      default:
        return null;
    }
    let recurringIntervalCode = null;
    let payments = null;
    switch (recurringInterval) {
      case 'monthly':
        recurringIntervalCode = day + ' * *';
        break;
      case 'quarterly':
        payments = [month, month + 3, month + 6, month + 9];
        for (const i in payments) {
          if (payments[i] > 12) {
            payments[i] = payments[i] - 12;
          }
        }
        recurringIntervalCode = day + ' ' + payments.join(',') + ' *';
        break;
      case 'semestral':
        payments = [month, month + 6];
        for (const i in payments) {
          if (payments[i] > 12) {
            payments[i] = payments[i] - 12;
          }
        }
        recurringIntervalCode = day + ' ' + payments.join(',') + ' *';
        break;
      case 'yearly':
        recurringIntervalCode = day + ' ' + month + ' *';
        break;
    }
    return recurringIntervalCode;
  }

  getSextantSalutation(titleCode) {
    const codeLabels = {
      '0': 'SALUTATION_MALE',
      '1': 'SALUTATION_FEMALE',
      '2': 'SALUTATION_FAMILY',
      '3': 'SALUTATION_COMPANY',
      '4': 'SALUTATION_OTHER',
      '5': 'SALUTATION_FEMALE_UNKNOWN',
      '6': 'SALUTATION_FEMALE_UNMARRIED',
      '7': 'SALUTATION_NONE',
      '8': 'SALUTATION_DR',
    };
    const RAISENOW_SALUTATION_CHOICES = {
      /* eslint-disable @typescript-eslint/naming-convention */
      SALUTATION_MALE: 'mr',
      SALUTATION_FEMALE: 'ms',
      SALUTATION_FEMALE_UNKNOWN: 'ms',
      SALUTATION_FEMALE_UNMARRIED: 'ms',
      SALUTATION_FAMILY: 'family',
      SALUTATION_COMPANY: 'none',
      SALUTATION_OTHER: 'other',
      /* eslint-enable @typescript-eslint/naming-convention */
    };
    const label = codeLabels[titleCode];
    return (
      RAISENOW_SALUTATION_CHOICES[label] ||
      RAISENOW_SALUTATION_CHOICES['SALUTATION_OTHER']
    );
  }

  /**
   * get a "human readable" field value that we send to RaiseNow for Salesforce
   * @param  field campaign field
   * @param  value our raw value
   * @return       the desired value
   */
  getRaiseNowAdditionalFieldValue(field: CampaignField, value: unknown) {
    switch (field.widget) {
      case 'radioToggle':
      case 'radiosWithContent':
      case 'select':
        const fieldItem =
          isCampaignSelectField(field) &&
          field.widgetOptions.items.find((item) => item.value === value);
        if (fieldItem) {
          return this.translationService.getInlineTranslation(fieldItem.label);
        }
        return '';
      case 'checkboxes':
        if (isCampaignSelectField(field) && Array.isArray(value)) {
          const itemLabels: string[] = [];
          field.widgetOptions.items.forEach((item) => {
            if (value.indexOf(item.value) > -1) {
              itemLabels.push(
                this.translationService.getInlineTranslation(item.label),
              );
            }
          });
          return itemLabels.join(',');
        } else {
          return '';
        }
      default:
        return value;
    }
  }
  /**
   * get through all the fields and filter "additional fields"
   * @param  formData   filled-out form data
   * @param  campaign   campaign properties
   * @return object mapping an API-defined key to a "human readable" value
   */
  prepareAdditionalFields(
    formData: Record<string, unknown>,
    campaign: Campaign,
  ) {
    // get through all the fields and filter "additional fields"
    const allFieldsets = campaign.pages.reduce(
      (a, v) => a.concat(v.fieldsets),
      [] as CampaignFieldset[],
    );
    const additionalFields = allFieldsets
      .reduce((acc, fs) => acc.concat(fs.fields), [] as CampaignField[])
      .filter(
        (f): f is CampaignField & { apiFieldName: string } =>
          f.isAdditionalField === true && isNonEmptyString(f.apiFieldName),
      );
    const additionalFieldsMapping = Object.assign(
      {},
      ...additionalFields.map((field) => ({
        [field.apiFieldName]: this.getRaiseNowAdditionalFieldValue(
          field,
          formData[field.key],
        ),
      })),
    );
    return additionalFieldsMapping;
  }

  /**
   * Here we collect all the variables from the different input objects,
   * without any logic. The exact logic (Salesforce / Sextant) is determined in
   * the method _createEpikPaymentObjectBase called later with the fields we create here.
   * @param  formData   filled-out form data
   * @param  fundraiser fundraiser properties
   * @param  campaign   campaign properties
   * @return            an object with all the fields RaiseNow needs
   */
  getRaiseNowExtraFields(
    formData: Record<string, any>,
    fundraiser: FundraiserMinimal,
    campaign: Campaign,
  ): Readonly<RaiseNowExtraFields> {
    const fields: RaiseNowExtraFields = {};

    // Initially set raisenow test mode based on environment setting
    fields.raisenowTestMode = this.rnwSettings.testMode();

    // get the salutation
    fields.title = this.getSextantSalutation(formData.title);
    // get the DNI number
    const dniField = campaign.pages[0].fieldsets[0].fields.find(
      (field) =>
        isRecord(field.label) && Object.values(field.label).includes('DNI'),
    );
    if (dniField && 'key' in dniField) {
      fields.dni = formData[dniField.key];
    }
    fields.additionalFieldsMapping = this.prepareAdditionalFields(
      formData,
      campaign,
    );
    // get the translated occupation & academic title labels
    fields.occupationLabel = this.getFieldItemLabel(
      campaign,
      'occupation',
      formData.occupation,
    );
    fields.academicTitleLabel = this.getFieldItemLabel(
      campaign,
      'academic_title',
      formData.academic_title,
    );
    // set currency from form
    if (formData.currency) {
      fields.raisenowCurrencyCode = this.formatCurrencyCode(formData.currency);
    }

    // the raisenow_config parameters
    if (campaign.raisenow) {
      if ('raisenow_api_key' in campaign.raisenow) {
        fields.raisenowApiKey = campaign.raisenow.raisenow_api_key;
      }
      if ('raisenow_test_mode' in campaign.raisenow) {
        fields.raisenowTestMode = campaign.raisenow.raisenow_test_mode;
      }
      if ('email_fallback_address' in campaign.raisenow) {
        fields.email_fallback_address =
          campaign.raisenow.email_fallback_address;
      }
      if ('stored_campaign_id' in campaign.raisenow) {
        fields.stored_campaign_id = campaign.raisenow.stored_campaign_id;
      }
      if ('stored_customer_email_permission' in campaign.raisenow) {
        fields.stored_customer_email_permission =
          campaign.raisenow.stored_customer_email_permission;
      }
      if (
        'raisenow_currency_code' in campaign.raisenow &&
        !fields.raisenowCurrencyCode
      ) {
        fields.raisenowCurrencyCode = this.formatCurrencyCode(
          campaign.raisenow.raisenow_currency_code,
        );
      }
      if ('raisenow_use_stripe' in campaign.raisenow) {
        fields.raisenowUseStripe = campaign.raisenow.raisenow_use_stripe;
      }
      if ('raisenow_use_salesforce' in campaign.raisenow) {
        fields.raisenowUseSalesforce =
          campaign.raisenow.raisenow_use_salesforce;
      }
      if ('account_uuid' in campaign.raisenow) {
        fields.accountUuid = campaign.raisenow.account_uuid;
      }
      if ('profile' in campaign.raisenow) {
        fields.profile = campaign.raisenow.profile;
      }
      // TODO: properly get the timezone when it's in the API and remove hard-coded values
      if ('timezone' in campaign.raisenow) {
        fields.timezone = campaign.raisenow.timezone;
      }
      if ('statement_descriptor' in campaign.raisenow) {
        fields.statementDescriptor = campaign.raisenow.statement_descriptor;
      }
      if ('skip_cvv' in campaign.raisenow) {
        fields.skipCvv = campaign.raisenow.skip_cvv;
      } else {
        fields.skipCvv = true;
      }
      // Salesforce variables
      if ('stored_fundraising_team_type' in campaign.raisenow) {
        fields.stored_fundraising_team_type =
          campaign.raisenow.stored_fundraising_team_type;
      }
      if ('stored_crm_fundraising_team' in campaign.raisenow) {
        fields.stored_crm_fundraising_team =
          campaign.raisenow.stored_crm_fundraising_team;
      }
      if ('fundraiserCode' in fundraiser && fundraiser.fundraiserCode) {
        fields.fundraiserCode = fundraiser.fundraiserCode;
      }
      if (
        'fundraiserExternalId' in fundraiser &&
        fundraiser.fundraiserExternalId
      ) {
        fields.fundraiserExternalId = fundraiser.fundraiserExternalId;
      }
      if ('firstName' in fundraiser && fundraiser.firstName) {
        fields.fundraiser_firstname = fundraiser.firstName;
      }
      if ('lastName' in fundraiser && fundraiser.lastName) {
        fields.fundraiser_lastname = fundraiser.lastName;
      }
      if ('email' in fundraiser && fundraiser.email) {
        fields.fundraiser_email = fundraiser.email;
      }
    }

    return fields;
  }
}
